import React, { useState } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons } from './icons';
import { NOMS_LANGUES, t, UI_LANGUAGES, type UiLanguage } from '../i18n';
import { useLanguageStore } from '../store/languageStore';
import { choisirLangue } from './UiLanguagePicker';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';

/**
 * Sélecteur de langue discret, pour l'écran d'accueil AVANT connexion.
 *
 * C'est le seul écran où le choix n'est pas rattrapable : le sélecteur complet vit dans les
 * Réglages, donc derrière l'authentification. Sans lui, quelqu'un dont le téléphone est en anglais
 * verrait cet écran en anglais sans aucun moyen d'en changer — et c'est précisément l'écran où l'on
 * décide de créer un compte, ou pas.
 *
 * Il écrit la MÊME préférence que le sélecteur des Réglages : le choix survit à la création de
 * compte, et la détection d'appareil ne le réécrase jamais.
 *
 * Un bouton, puis une liste : six codes « FR | EN | … » ne tiennent plus face au logo centré (la place
 * réservée de l'autre côté fait 56 points). Chaque langue y est écrite dans sa propre langue.
 *
 * Pas de drapeau : un drapeau désigne un pays, pas une langue.
 */
export interface CompactLanguagePickerProps {
  testID?: string;
  // 'sombre' : bouton à voile blanc et texte blanc, pour l'accueil bleu nuit. La liste, elle, reste
  // claire : c'est une feuille posée par-dessus, pas un prolongement du fond.
  surFond?: 'clair' | 'sombre';
}

export function CompactLanguagePicker({ testID = 'lang-compact', surFond = 'clair' }: CompactLanguagePickerProps) {
  const sombre = surFond === 'sombre';
  const { language, change } = useLanguageStore();
  const [ouvert, setOuvert] = useState(false);

  const choisir = (option: UiLanguage) => {
    setOuvert(false);
    choisirLangue(option, language, change);
  };

  return (
    <>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={t('settings.language.label')}
        accessibilityValue={{ text: NOMS_LANGUES[language] }}
        onPress={() => setOuvert(true)}
        hitSlop={8}
        style={[styles.bouton, sombre && styles.boutonSombre]}
      >
        {sombre ? <Ionicons name="globe-outline" size={15} color={colors.white} /> : null}
        <Text style={[styles.code, sombre && styles.codeSombre]}>{language.toUpperCase()}</Text>
        {sombre ? null : <Ionicons name="chevron-down" size={14} color={colors.electric} />}
      </Pressable>

      <Modal visible={ouvert} transparent animationType="fade" onRequestClose={() => setOuvert(false)}>
        <Pressable style={styles.voile} onPress={() => setOuvert(false)} testID={`${testID}-voile`}>
          <View style={styles.liste} accessibilityRole="radiogroup">
            {UI_LANGUAGES.map((option) => {
              const actif = option === language;
              return (
                <Pressable
                  key={option}
                  testID={`${testID}-${option}`}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: actif }}
                  onPress={() => choisir(option)}
                  style={[styles.ligne, actif && styles.ligneActive]}
                >
                  <Text style={[styles.nom, actif && styles.nomActif]}>{NOMS_LANGUES[option]}</Text>
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  bouton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radius.pill,
  },
  // Bouton de la maquette : 34 pt de haut, la zone tactile de 44 pt venant de `hitSlop`.
  boutonSombre: {
    height: 34,
    paddingHorizontal: spacing.md,
    paddingVertical: 0,
    gap: 6,
    borderWidth: 1,
    borderColor: colors.bordureNuit,
    backgroundColor: colors.surfaceNuit,
  },
  code: { fontSize: typography.small.size, fontWeight: '700', color: colors.electric },
  codeSombre: { fontWeight: '600', color: colors.white },
  voile: { flex: 1, backgroundColor: colors.voile, justifyContent: 'center', padding: screenPadding },
  liste: { gap: spacing.xs, padding: spacing.md, borderRadius: radius.xl, backgroundColor: colors.white },
  ligne: { minHeight: 44, justifyContent: 'center', paddingHorizontal: spacing.lg, borderRadius: radius.md },
  ligneActive: { backgroundColor: colors.electric },
  nom: { fontSize: typography.body.size, color: colors.night },
  nomActif: { color: colors.white, fontWeight: '700' },
});
